/** @fileoverview Renders Platform sign-in and workspace selection states. */

import { ArrowRight } from 'lucide-react';
import { Alert, AlertAction, AlertDescription } from './ui/alert';
import { Button } from './ui/button';
import LoadingState from './ui/loading-state';
import type { Tenant, User } from '../types/identity';

type Props = {
  user?: User;
  tenants: Tenant[];
  tenantId: string;
  error: string;
  /** A workspace will be picked automatically, so keep showing the loading state. */
  autoSelecting?: boolean;
  onSelect: (tenantId: string) => void;
  onLogin: () => void;
};

/**
 * Shows loading, access, error, and tenant selection states before chat opens.
 * @param props - User, accessible tenants, current route tenant, and selection/login actions.
 */
export default function WorkspacePicker({
  user,
  tenants,
  tenantId,
  error,
  autoSelecting,
  onSelect,
  onLogin,
}: Props) {
  if ((!user && !error) || autoSelecting) {
    return (
      <main className="flex min-h-0 flex-1 items-center justify-center bg-app-surface-muted">
        <LoadingState>Loading your workspace…</LoadingState>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-5xl px-8 py-16">
      <span className="text-xs font-bold tracking-widest text-app-brand">
        YOUR IDEAS, TAKING SHAPE
      </span>
      <h1 className="text-4xl font-bold tracking-tight text-app-text">Welcome to Fabrik.</h1>
      <p className="leading-relaxed text-app-text-muted">
        {user && !tenants.length
          ? 'Your account does not have Fabrik access yet. Contact your workspace administrator.'
          : 'Choose a workspace to get started.'}
      </p>
      {error && (
        <Alert className="my-3 max-w-4xl border-app-danger-border bg-app-danger-surface text-app-danger">
          <AlertDescription className="text-xs text-app-danger">{error}</AlertDescription>
          <AlertAction>
            <Button
              variant="link"
              className="h-auto shrink-0 p-0 text-xs text-app-danger"
              type="button"
              onClick={onLogin}
            >
              Sign in with Platform
            </Button>
          </AlertAction>
        </Alert>
      )}
      {tenantId && user && !tenants.some((tenant) => tenant.tenant_id === tenantId) && (
        <Alert variant="destructive" className="mt-3 max-w-4xl text-xs">
          <AlertDescription className="text-xs">
            This workspace is not available to your account.
          </AlertDescription>
        </Alert>
      )}
      <div className="mt-9 flex flex-wrap gap-4">
        {tenants.map((tenant) => (
          <Button
            variant="outline"
            className="h-auto min-w-56 flex-col items-start rounded-lg border-app-border bg-app-surface-card p-6 text-left font-semibold whitespace-normal text-app-text"
            key={tenant.tenant_id}
            type="button"
            onClick={() => onSelect(tenant.tenant_id)}
          >
            {tenant.name}
            <span className="mt-5 flex items-center gap-1 text-xs font-normal text-app-brand">
              Open workspace <ArrowRight className="h-3 w-3" aria-hidden="true" />
            </span>
          </Button>
        ))}
      </div>
    </main>
  );
}
